/**
 * Auth Callback Page
 *
 * Handles the OAuth callback from WorkOS AuthKit.
 * After WorkOS authentication, users are redirected here.
 * The AuthKit SDK automatically handles the token exchange.
 *
 * This component:
 * 1. Shows a loading state while AuthKit processes the callback
 * 2. Redirects to the appropriate dashboard based on user role
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@shared/context';
import { showToast } from '@shared/components/Toast';
import { useMutation, useApolloClient } from '@apollo/client';
import { RECORD_REGISTRATION_CONSENT } from '../../graphql/mutations/user/record-registration-consent.mutation';
import { GET_USER } from '../../graphql/queries/user/get-user.query';
import {
  consumeRegistrationConsent,
  peekRegistrationConsent,
} from '../../shared/registration-consent';
import { consumePostLoginRedirect } from '../../shared/post-login-redirect';
import { getDefaultRouteForRoles } from '../../shared/role-default-route';

// How long to wait for auth to complete before showing error (ms)
const AUTH_TIMEOUT_MS = 10000;

export default function AuthCallback() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRedirectedRef = useRef(false);
  const [recordConsent] = useMutation(RECORD_REGISTRATION_CONSENT);
  const apollo = useApolloClient();

  // Set up a timeout - if auth doesn't complete in time, show error
  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    // If there's an explicit error param from WorkOS, show error immediately
    if (error) {
      setHasError(true);
      return;
    }

    // Only set timeout if we have a code (i.e., we're in the middle of auth flow)
    if (code && !hasRedirectedRef.current) {
      timeoutRef.current = setTimeout(() => {
        // Only show error if we still haven't authenticated
        if (!hasRedirectedRef.current) {
          console.warn('[AuthCallback] Auth timeout - showing error');
          setHasError(true);
        }
      }, AUTH_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchParams]);

  // Handle auth state changes - redirect when ready
  useEffect(() => {
    const code = searchParams.get('code');

    // Wait for auth to complete loading
    if (isLoading) {
      return;
    }

    if (isAuthenticated && user) {
      // Intercept LOCKED users — redirect away before any consent or
      // role-based routing. This catches the initial OAuth callback path;
      // the Shell gate catches session-resume and refresh scenarios.
      if (user.status === 'LOCKED') {
        hasRedirectedRef.current = true;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        navigate('/locked-user', { replace: true });
        return;
      }

      // Guard against the effect firing twice (StrictMode in dev, or React
      // re-running on a state change between the auth check and navigate).
      if (hasRedirectedRef.current) return;
      hasRedirectedRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // P1-5 fix: prefer the deep-link target stashed before the WorkOS
      // round-trip (sessionStorage). Fall back to the role's default
      // route. Validation + clear happens inside consumePostLoginRedirect.
      const stashedTarget = consumePostLoginRedirect();
      const redirectPath = stashedTarget ?? getDefaultRouteForRoles(user.roles);

      // Read the consent stashed at /join (TOS + SMS opt-in + the click
      // timestamp). Peek first so a transient failure doesn't permanently
      // lose the consent — we only consume after a successful write.
      //
      // The presence of a consent stash also tells us this is a fresh
      // registration (vs. a returning login) — only /join sets it, and
      // it's cleared after the first successful auth-callback. Use that
      // signal to fire a one-time "Email verified" welcome toast so the
      // customer sees an explicit confirmation that their verification
      // succeeded, not just a silent redirect to the dashboard. Closes
      // 4FBAD9 (Ruth: "No confirmation message after email verification").
      const consent = peekRegistrationConsent();
      const finishRedirect = async () => {
        if (consent) {
          // Fire the welcome toast before the consent-record mutation
          // and navigation; sonner toasts persist across route changes
          // so the customer sees it on their landing page. We fire here
          // (not inside the try/success branch) because the consent
          // mutation succeeding or failing transiently doesn't change
          // the customer's experience — they're authenticated either
          // way, and the email-verified message is true regardless.
          showToast.success(
            'Email verified — welcome to eSignature Guarantee',
            "You're signed in. Let's get your transaction started.",
          );
          try {
            const result = await recordConsent({
              variables: {
                tosAcceptedAt: consent.tosAcceptedAt,
                smsConsent: consent.smsConsent,
              },
            });
            // Apollo's errorPolicy: 'all' on mutations means backend errors
            // come back on result.errors instead of throwing. Surface them
            // so we know the mutation didn't actually persist; leave the
            // stash in place so a retry on the next page load can recover.
            if (result.errors?.length) {
              console.warn('[AuthCallback] recordRegistrationConsent returned errors', result.errors);
            } else if (!result.data?.recordRegistrationConsent?.tosAcceptedAt) {
              console.warn('[AuthCallback] recordRegistrationConsent returned no tosAcceptedAt', result.data);
            } else {
              // Success — clear the stash and refresh GET_USER so any
              // subscriber sees the new state.
              consumeRegistrationConsent();
              try {
                await apollo.refetchQueries({ include: [GET_USER] });
              } catch (refetchErr) {
                console.warn('[AuthCallback] GET_USER refetch failed (non-blocking)', refetchErr);
              }
            }
          } catch (err) {
            // Don't block the redirect on a failed mutation — the user is
            // authenticated. The stash stays in sessionStorage; a retry
            // can fire from a top-level effect on the next page load.
            console.warn('[AuthCallback] recordRegistrationConsent mutation threw', err);
          }
        }
        navigate(redirectPath, { replace: true });
      };
      void finishRedirect();
    } else if (!code && !isAuthenticated) {
      // No code and no auth - this isn't a callback, redirect to login
      navigate('/login', { replace: true });
    }
    // If we have a code but aren't authenticated yet, just wait -
    // the timeout will handle the error case
  }, [isAuthenticated, isLoading, user, navigate, searchParams, recordConsent, apollo]);

  if (hasError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ color: 'var(--color-error-500, #d32f2f)', marginBottom: 16 }}>Authentication Failed</h2>
        <p style={{ color: 'var(--color-text-muted, #78716C)', marginBottom: 24, textAlign: 'center', maxWidth: 400 }}>
          There was a problem signing you in. This can happen if the sign-in session expired.
        </p>
        <button
          onClick={() => {
            // Clear any stale state and go to login
            localStorage.removeItem('token');
            window.location.href = '/login';
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--color-primary-400, #2ECC71)',
            color: 'var(--color-neutral-900, #0B2B17)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div className="spinner" aria-label="Loading..." />
      <p style={{ marginTop: 16, color: 'var(--color-text-muted, #78716C)' }}>Completing sign in...</p>
    </div>
  );
}
